import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Company } from '../types/models';
import { CompanyService } from '../services/companyService';
import { setCurrentCompanyId } from '../services/api';

// Простые хелперы для работы с cookie
const setCookie = (name: string, value: string, days: number) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/`;
};

const getCookie = (name: string): string | null => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

interface CompanyContextType {
  selectedCompany: Company | null;
  companies: Company[];
  setSelectedCompany: (company: Company | null) => void;
  loading: boolean;
  error: string | null;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

interface CompanyProviderProps {
  children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await CompanyService.getUserCompanies();

      if (result.success && result.data) {
        setCompanies(result.data);
        // Попробуем восстановить выбор из cookie, иначе возьмём первый
        const cookieCompanyId = getCookie('selectedCompanyId');
        const savedId = cookieCompanyId ? parseInt(cookieCompanyId) : null;
        const found = savedId ? result.data.find(c => c.id === savedId) || null : null;

        const companyToSelect = found || (result.data.length > 0 ? result.data[0] : null);
        if (companyToSelect) {
          setSelectedCompany(companyToSelect);
          setCurrentCompanyId(companyToSelect.id);
        } else {
          setCurrentCompanyId(null);
        }
      } else {
        setError(result.message || 'Не удалось загрузить список контрагентов');
      }
    } catch (err) {
      setError('Ошибка при загрузке контрагентов');
      console.error('Error loading companies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetSelectedCompany = (company: Company | null) => {
    setSelectedCompany(company);
    setCurrentCompanyId(company?.id || null);
    if (company?.id) {
      setCookie('selectedCompanyId', String(company.id), 30);
    } else {
      setCookie('selectedCompanyId', '', -1);
    }
  };

  const value: CompanyContextType = {
    selectedCompany,
    companies,
    setSelectedCompany: handleSetSelectedCompany,
    loading,
    error,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};
